import IconAdd from "@shared/components/icons/Add"
import IconEllipsis from "@shared/components/icons/Ellipsis"
import IconMessagesBubbleSquareAdd from "@shared/components/icons/MessagesBubbleSquareAdd"
import { cx } from "@shared/components/untitledui/util/cx"
import type { CardAttrsBase } from "@shared/unified-tiptap/util/card"
import { tracked } from "@supergrain/kernel/react"
import type { Node as PMNode } from "@tiptap/pm/model"
import type { ReactNodeViewProps } from "@tiptap/react"
import { useState } from "react"
import { reportCardMenuOpenChange } from "../../analytics/open-menu-event"
import type { Link } from "../../extensions/link/link-attrs"
import { useUiContext } from "../../unified-tiptap-ui-context"
import { AddButton } from "../add-button/AddButton"
import { CardCommentButton } from "./CardCommentButton"
import { CardMenu } from "./CardMenu"
import { CardPillButton } from "./CardPillButton"

/**
 * Fixed buttons at the top right of a card (the "New Menus" design): the card menu
 * (ellipsis) and the Add button (plus). Shared by every card node view — each supplies
 * the parts that are its own (where Add inserts, what Paste does, where the column
 * hangs); everything editor-wide comes from the UI context.
 *
 * `getPos()` must point at the card node itself.
 */
export const CardButtons = tracked(
  (
    props: ReactNodeViewProps & {
      /** Which Add menu this card offers — see AddButtonProps `mode`. */
      mode: "titledCard" | "cardGoogleClassroom"
      /** Where the Add menu inserts, as a findAddTargetPos-style position. */
      getAddTargetPos: () => number | null
      /** The column's position and reveal paint, from the card's own CSS module. */
      className: string
      /** When set, attachment picks land here instead of being inserted as block nodes. */
      addAttachment?: (link: Link) => void
      /** Replace this card with a copied card of the same type. */
      pasteCard: (copiedCard: PMNode) => void
      canPostToGoogleClassroom?: boolean
    },
  ) => {
    const { editor, node, getPos, mode, getAddTargetPos, addAttachment } = props
    const { permissions, hideButtons, setCardOverlayOpen } = useUiContext()

    const getCardId = () => (node.attrs as CardAttrsBase).id

    const addButtonType = editor.isEditable ? "add" : permissions.canComment ? "comment" : null

    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isAddOpen, setIsAddOpen] = useState(false)

    const setAnyOverlayOpen = (next: { addOpen?: boolean; menuOpen?: boolean }) => {
      const addOpen = next.addOpen ?? isAddOpen
      const menuOpen = next.menuOpen ?? isMenuOpen
      setCardOverlayOpen(addOpen || menuOpen)
    }

    const setAddOpen = (isOpen: boolean) => {
      setIsAddOpen(isOpen)
      setAnyOverlayOpen({ addOpen: isOpen })
    }

    const setMenuOpen = (isOpen: boolean) => {
      setIsMenuOpen(isOpen)
      setAnyOverlayOpen({ menuOpen: isOpen })
      reportCardMenuOpenChange(editor, { getPos: () => getPos() ?? null, isOpen })
    }

    if (hideButtons) return null

    return (
      // Absolutely positioned so the buttons take NO layout space — the card's text lines
      // always keep their full width. Each card type places the column itself (className).
      //
      // The two buttons stack (Add above the menu) and abut (no gap) into one stadium-shaped
      // pill, so the shadow has to be the group's: drop-shadow follows the union of both
      // halves, leaving no seam. Both labels grow right into the margin — nothing sits beside
      // either button.
      <div
        className={cx("absolute z-[1] flex flex-col card-pill-shadow", props.className)}
        contentEditable={false}
        data-card-buttons
      >
        {addButtonType === "add" && (
          <AddButton
            editor={editor}
            mode={mode}
            getAddTargetPos={getAddTargetPos}
            getCardId={getCardId}
            onOpenChange={setAddOpen}
            addAttachment={addAttachment}
          >
            <CardPillButton
              ariaLabel="Add to card"
              label="Add"
              icon={<IconAdd />}
              isMenuOpen={isAddOpen}
              attachedSide="bottom"
            />
          </AddButton>
        )}
        {addButtonType === "comment" && (
          <CardCommentButton editor={editor} getCardId={getCardId} onOpenChange={setAddOpen}>
            <CardPillButton
              ariaLabel="Comment on card"
              label="Comment"
              icon={<IconMessagesBubbleSquareAdd />}
              isMenuOpen={isAddOpen}
              attachedSide="bottom"
            />
          </CardCommentButton>
        )}
        <CardMenu {...props} onOpenChange={setMenuOpen}>
          <CardPillButton
            ariaLabel="Card menu"
            label="Menu"
            icon={<IconEllipsis />}
            isMenuOpen={isMenuOpen}
            attachedSide={addButtonType !== null ? "top" : undefined}
          />
        </CardMenu>
      </div>
    )
  },
)
